/**
 * GovNotesTab — residential-style notes with @-mention autocomplete + pinning.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';

interface User { id: string; name?: string; email?: string; }
interface Note {
  id: string; body: string; author_id?: string; author_name?: string;
  pinned: boolean; mentioned_user_ids?: string[];
  edited_at?: string; created_at: string;
}

const S: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column' as const, gap: 16 },
  composer: {
    background: '#1a2b3c', border: '1px solid #2d4259', borderRadius: 12,
    padding: 16, position: 'relative' as const,
  },
  ta: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 8,
    padding: 12, color: '#e5edf5', fontSize: 14, width: '100%',
    boxSizing: 'border-box' as const, minHeight: 100,
    resize: 'vertical' as const, fontFamily: 'inherit', lineHeight: 1.5,
  },
  bar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 10,
  },
  hint: { fontSize: 11, color: '#8fa8c0' },
  postBtn: {
    background: '#4a7eb5', color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
  postBtnDisabled: {
    background: '#334155', color: '#64748b', border: 'none', borderRadius: 8,
    padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'not-allowed',
  },
  mentionPop: {
    position: 'absolute' as const, background: '#243447', border: '1px solid #4a7eb5',
    borderRadius: 8, padding: 4, minWidth: 220, zIndex: 100,
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)', maxHeight: 220, overflow: 'auto' as const,
  },
  mentionItem: {
    padding: '8px 12px', color: '#e5edf5', fontSize: 13, cursor: 'pointer',
    borderRadius: 6,
  },
  mentionItemActive: { background: '#4a7eb5', color: '#fff' },
  note: {
    background: '#1a2b3c', border: '1px solid #2d4259', borderRadius: 12,
    padding: 14, position: 'relative' as const,
  },
  notePinned: { borderColor: '#eab308', background: '#1a2519' },
  noteHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    marginBottom: 8, gap: 8,
  },
  authorRow: { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  avatar: {
    width: 28, height: 28, borderRadius: 14, background: '#4a7eb5',
    color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex',
    alignItems: 'center' as const, justifyContent: 'center',
  },
  author: { fontSize: 13, fontWeight: 700, color: '#e5edf5' },
  time: { fontSize: 11, color: '#8fa8c0' },
  actions: { display: 'flex', gap: 4 },
  actBtn: {
    background: 'transparent', color: '#8fa8c0', border: 'none',
    padding: 4, cursor: 'pointer', fontSize: 14, borderRadius: 4,
  },
  body: {
    fontSize: 14, color: '#e5edf5', lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const,
  },
  mention: {
    background: '#4a7eb555', color: '#a3c5e5', padding: '1px 6px',
    borderRadius: 4, fontWeight: 700,
  },
  empty: { textAlign: 'center' as const, color: '#526980', padding: 40 },
};

// Render note body with @-mentions highlighted
function renderBody(body: string, users: User[]): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /@([a-z0-9._-]+@[a-z0-9._-]+\.[a-z]{2,}|[a-z][a-z0-9._-]*)/gi;
  let lastIdx = 0;
  let m;
  let i = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIdx) parts.push(body.slice(lastIdx, m.index));
    const handle = m[1];
    const user = users.find((u) => u.email?.toLowerCase().includes(handle.toLowerCase()) ||
                                    u.name?.toLowerCase().includes(handle.toLowerCase()));
    const label = user ? `@${user.name || user.email}` : m[0];
    parts.push(<span key={i++} style={S.mention}>{label}</span>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return parts;
}

const GovNotesTab: React.FC<{
  solicitationId: string;
  users: User[];
  currentUserId?: string;
}> = ({ solicitationId, users, currentUserId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [caretPos, setCaretPos] = useState(0);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/solicitations/${solicitationId}/notes`);
      setNotes(data?.notes || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [solicitationId]);
  useEffect(() => { load(); }, [load]);

  // Detect @-mention typing
  const onDraftChange = (val: string, caret: number) => {
    setDraft(val);
    setCaretPos(caret);
    // find "@" not preceded by a word char, up to caret
    const upToCaret = val.slice(0, caret);
    const m = upToCaret.match(/(^|\s)@([a-z0-9._-]*)$/i);
    if (m) {
      setMentionQuery(m[2]);
      setMentionIdx(0);
    } else {
      setMentionQuery(null);
    }
  };

  const filteredUsers = mentionQuery !== null
    ? users.filter((u) => {
        const q = mentionQuery.toLowerCase();
        return (u.name || '').toLowerCase().includes(q) ||
               (u.email || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  const insertMention = (user: User) => {
    const handle = user.email?.split('@')[0] || user.name?.replace(/\s+/g, '.').toLowerCase() || user.id;
    // Replace the @query with @handle
    const before = draft.slice(0, caretPos);
    const after = draft.slice(caretPos);
    const replaced = before.replace(/@[a-z0-9._-]*$/i, `@${handle} `);
    const newVal = replaced + after;
    setDraft(newVal);
    setMentionQuery(null);
    // Restore caret at end of insertion
    setTimeout(() => {
      taRef.current?.focus();
      const newCaret = replaced.length;
      taRef.current?.setSelectionRange(newCaret, newCaret);
    }, 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => Math.min(i + 1, filteredUsers.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => Math.max(0, i - 1)); }
      else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filteredUsers[mentionIdx]); }
      else if (e.key === 'Escape') { setMentionQuery(null); }
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      post();
    }
  };

  const post = async () => {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/gov/solicitations/${solicitationId}/notes`, { body });
      setNotes((prev) => [data.note, ...prev]);
      setDraft('');
      setMentionQuery(null);
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e.message}`); }
    finally { setPosting(false); }
  };

  const togglePin = async (n: Note) => {
    try {
      await api.patch(`/gov/notes/${n.id}`, { pinned: !n.pinned });
      load();
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e.message}`); }
  };

  const del = async (n: Note) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/gov/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e.message}`); }
  };

  const initials = (name?: string) => {
    if (!name) return '?';
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
  };

  if (loading) return <div style={S.empty}>Loading notes…</div>;

  return (
    <div style={S.wrap}>
      {/* Composer */}
      <div style={S.composer}>
        <textarea
          ref={taRef}
          style={S.ta}
          placeholder="Write a note… use @ to mention someone."
          value={draft}
          onChange={(e) => onDraftChange(e.target.value, e.target.selectionStart || 0)}
          onKeyDown={onKeyDown}
          onSelect={(e) => setCaretPos((e.target as HTMLTextAreaElement).selectionStart || 0)}
        />
        {mentionQuery !== null && filteredUsers.length > 0 && (
          <div style={{ ...S.mentionPop, top: 130, left: 20 }}>
            {filteredUsers.map((u, i) => (
              <div
                key={u.id}
                style={{ ...S.mentionItem, ...(i === mentionIdx ? S.mentionItemActive : {}) }}
                onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                onMouseEnter={() => setMentionIdx(i)}
              >
                <b>{u.name || u.email}</b>
                {u.email && u.name && <span style={{ color: '#8fa8c0', marginLeft: 8, fontSize: 11 }}>{u.email}</span>}
              </div>
            ))}
          </div>
        )}
        <div style={S.bar}>
          <div style={S.hint}>
            {mentionQuery !== null
              ? `Type to search, ↑↓ to select, Enter to insert`
              : `Type @ to mention · ⌘+Enter to post`}
          </div>
          <button
            style={draft.trim() && !posting ? S.postBtn : S.postBtnDisabled}
            disabled={!draft.trim() || posting}
            onClick={post}
          >
            {posting ? 'Posting…' : 'Post Note'}
          </button>
        </div>
      </div>

      {/* Notes list */}
      {notes.length === 0 && <div style={S.empty}>No notes yet. Be the first to write one.</div>}
      {notes.map((n) => (
        <div key={n.id} style={{ ...S.note, ...(n.pinned ? S.notePinned : {}) }}>
          <div style={S.noteHead}>
            <div style={S.authorRow}>
              <div style={S.avatar}>{initials(n.author_name)}</div>
              <div>
                <div style={S.author}>{n.author_name || 'Unknown'}</div>
                <div style={S.time}>
                  {format(parseISO(n.created_at), 'MMM d, yyyy · h:mm a')}
                  {n.edited_at && ' · edited'}
                  {n.pinned && ' · 📌 Pinned'}
                </div>
              </div>
            </div>
            <div style={S.actions}>
              <button style={S.actBtn} onClick={() => togglePin(n)} title={n.pinned ? 'Unpin' : 'Pin'}>
                {n.pinned ? '📌' : '📍'}
              </button>
              {(n.author_id === currentUserId) && (
                <button style={S.actBtn} onClick={() => del(n)} title="Delete">🗑️</button>
              )}
            </div>
          </div>
          <div style={S.body}>{renderBody(n.body, users)}</div>
        </div>
      ))}
    </div>
  );
};

export default GovNotesTab;
